import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Button, Container, Group, Select, TextInput } from '@mantine/core';
import { AppState } from '../../client-redux/reducers';
import { Language } from '../../languages/languageHandler';
import ModalComponent from '../../components/modal';
import {
  addEquipmentDeclaration,
  addEquipmentReason,
  updateEquipmentReason,
  updateEquipmentDeclaration,
  Reason,
  Declaration
} from '../../../libraries/reason-declaration';
import { fetchLanguages, ILanguage } from '../../../libraries/language';
import { LoginState } from '../../client-redux/login/reducer';
import { isZarphSuccessResponse, useStatusOperDialog } from '../../context/status-oper-dialog';
import { StatusType } from '../../../libraries/zps';
import './styles.scss';

interface ReasonDeclarationProps {
  show: boolean;
  onClose: () => void;
  codeReasonDeclaration: number;
  language: number;
  text: string;
  type: 'reason' | 'declaration';
  isEditing: boolean;
  title: string;
  onReload: () => void;
}

const EquipmentReasonDeclaration: React.FC<ReasonDeclarationProps> = ({
  show,
  onClose,
  codeReasonDeclaration,
  language,
  title,
  text,
  isEditing,
  type,
  onReload
}) => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const loginInfo = useSelector<AppState, LoginState>(state => state.login);

  const [code, setCode] = useState<number>(codeReasonDeclaration);
  const [description, setDescription] = useState<string>(text);
  const [languages, setLanguages] = useState<ILanguage[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<number>();

  const controllerAddReason = new AbortController();
  const controllerAddDeclaration = new AbortController();

  const { showStatusOperDialog, handleApiError } = useStatusOperDialog();

  useEffect(() => {
    const fetchLanguageList = async () => {
      try {
        const response = await fetchLanguages();
        setLanguages(response.data.languages);
      } catch (error) {
        console.error('Error fetching languages:', error);
      }
    };
    fetchLanguageList();
  }, []);

  useEffect(() => {
    setSelectedLanguage(language);
    setCode(codeReasonDeclaration);
    setDescription(text);
  }, [codeReasonDeclaration, text, language]);

  const handleAddUpdate = async () => {
    let payload: Reason | Declaration;

    if (type === 'reason') {
      payload = {
        gid: loginInfo.user.gid,
        entid: loginInfo.user.entid,
        langid: Number(selectedLanguage),
        code: Number(code),
        reason: description
      } as Reason;
    } else {
      payload = {
        gid: loginInfo.user.gid,
        entid: loginInfo.user.entid,
        langid: Number(selectedLanguage),
        code: Number(code),
        declaration: description
      } as Declaration;
    }

    try {
      let response;

      if (isEditing) {
        if (type === 'reason') {
          response = await updateEquipmentReason(controllerAddReason, payload as Reason);
        } else {
          response = await updateEquipmentDeclaration(controllerAddDeclaration, payload as Declaration);
        }
      } else {
        if (type === 'reason') {
          response = await addEquipmentReason(controllerAddReason, payload as Reason);
        } else {
          response = await addEquipmentDeclaration(controllerAddDeclaration, payload as Declaration);
        }
      }

      if (!isZarphSuccessResponse(response)) {
        return showStatusOperDialog({
          type: StatusType.ERROR,
          message:
            response?.data?.statusOper?.message ||
            (isEditing
              ? type === 'reason'
                ? lang.UPDATE_REASON_ERROR
                : lang.UPDATE_DECLARATION_ERROR
              : type === 'reason'
                ? lang.ADD_REASON_ERROR
                : lang.ADD_DECLARATION_ERROR),
          statusOper: response?.data?.statusOper
        });
      }

      showStatusOperDialog({
        type: StatusType.INFO,
        message: isEditing
          ? type === 'reason'
            ? lang.UPDATE_REASON_SUCCESS
            : lang.UPDATE_DECLARATION_SUCCESS
          : type === 'reason'
            ? lang.ADD_REASON_SUCCESS
            : lang.ADD_DECLARATION_SUCCESS,
        statusOper: response?.data?.statusOper
      });

      onReload();
      onClose();
    } catch (error: any) {
      handleApiError(
        error,
        isEditing
          ? type === 'reason'
            ? lang.UPDATE_REASON_ERROR
            : lang.UPDATE_DECLARATION_ERROR
          : type === 'reason'
            ? lang.ADD_REASON_ERROR
            : lang.ADD_DECLARATION_ERROR
      );
    }
  };

  return (
    <ModalComponent
      show={show}
      handleClick={onClose}
      title={title}
      content={
        <Container className="add-reason-declaration">
          <Group>
            <Select
              label={lang.GLOBAL_LANGUAGE}
              value={selectedLanguage?.toString()}
              onChange={value => setSelectedLanguage(Number(value))}
              data={
                languages?.map(language => ({
                  value: language.langid.toString(),
                  label: language.nativeName
                })) || []
              }
              disabled={isEditing}
            />
            <TextInput
              label={lang.GLOBAL_CODE}
              value={code}
              onChange={e => setCode(Number(e.target.value))}
              disabled={isEditing}
            />
          </Group>

          <Group className="add-reason-declaration-description">
            <TextInput
              label={lang.EQUIPMENT_DESCRIPTION}
              value={description}
              onChange={e => setDescription(e.target.value)}
            />
          </Group>

          <Group>
            <Button color="#538539" radius="md" onClick={handleAddUpdate}>
              {isEditing ? lang.GLOBAL_UPDATE : lang.GLOBAL_ADD}
            </Button>
            <Button color="red" radius="md" onClick={onClose}>
              {lang.GLOBAL_CANCEL}
            </Button>
          </Group>
        </Container>
      }
    />
  );
};

export default EquipmentReasonDeclaration;
